import { Button, Checkbox, Input, Modal, Space, Typography } from 'antd';
import React, { FC, useEffect, useState } from 'react';
import { SegmentationLabel } from 'models/segmentation';

const { Text } = Typography;

interface IProps {
  isModalOpen: boolean;
  handleCancel: () => void;
  handleOk: (rejectedPredictionIcids: string[], comment: string) => void;
  segmentations: SegmentationLabel[];
  initialRejectedIds?: string[];
}

const RejectPredictionsModal: FC<IProps> = ({
  isModalOpen,
  handleCancel,
  handleOk,
  segmentations,
  initialRejectedIds = [],
}) => {
  const [comment, setComment] = useState('');
  const [rejected, setRejected] = useState<Set<string>>(new Set(initialRejectedIds));

  useEffect(() => {
    if (isModalOpen) {
      // default to selecting all predictions for faster technician reviews
      const allIds = segmentations.map(s => s.icid);
      setRejected(new Set(allIds));
      setComment('');
    }
  }, [isModalOpen, initialRejectedIds]);

  const handleCheckboxChange = (icid: string, checked: boolean) => {
    setRejected(prev => {
      const next = new Set(prev);
      if (checked) next.add(icid);
      else next.delete(icid);
      return next;
    });
  };

  const selectAll = () => setRejected(new Set(segmentations.map(s => s.icid)));
  const selectNone = () => setRejected(new Set());

  return (
    <Modal
      title="Select Predictions to Reject"
      open={isModalOpen}
      onCancel={handleCancel}
      footer={null}
      width={600}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <Button size="small" onClick={selectAll}>
              Select All
            </Button>
            <Button size="small" onClick={selectNone}>
              Select None
            </Button>
          </div>
          <Text type="secondary">
            Only selected predictions will be rejected; others will remain unchanged.
          </Text>
        </div>

        <div style={{ maxHeight: 260, overflowY: 'auto', paddingRight: 8 }}>
          {segmentations.map(seg => {
            const isRejected = rejected.has(seg.icid);
            return (
              <div key={seg.icid} style={{ marginBottom: 8 }}>
                <Checkbox
                  checked={isRejected}
                  onChange={e => handleCheckboxChange(seg.icid, e.target.checked)}
                >
                  <Space direction="vertical" size={0}>
                    <Text strong>{seg.segmentationClassValue}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {seg.icid}
                    </Text>
                  </Space>
                </Checkbox>
              </div>
            );
          })}
        </div>

        <div>
          <Text strong>Comment (optional):</Text>
          <Input.TextArea
            showCount
            rows={3}
            value={comment}
            onChange={e => setComment(e.target.value)}
            maxLength={255}
            placeholder="Add a comment for the rejected predictions..."
            style={{ marginTop: 8, resize: 'none' }}
          />
        </div>

        <Space style={{ width: '100%', justifyContent: 'space-between' }} size={12}>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button
            type="primary"
            onClick={() => handleOk(Array.from(rejected), comment)}
            disabled={rejected.size === 0}
          >
            Reject ({rejected.size} selected)
          </Button>
        </Space>
      </Space>
    </Modal>
  );
};

export default RejectPredictionsModal;
